// src/pages/AdminEventosPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';

const bs  = { fontFamily: "'Montserrat', sans-serif" };
const ds  = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const API = '/api';

const fmt = (d) => d ? new Date(d).toLocaleDateString('es-MX',
  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const BADGE = {
  pendiente:  { bg: '#fef3c7', color: '#92400e', label: 'Pendiente' },
  confirmado: { bg: '#d1fae5', color: '#065f46', label: 'Confirmado' },
  usado:      { bg: '#e0e7ff', color: '#3730a3', label: 'Usado' },
  cancelado:  { bg: '#fee2e2', color: '#991b1b', label: 'Cancelado' },
};

const Badge = ({ estado }) => {
  const b = BADGE[estado] || { bg: '#f3f4f6', color: '#374151', label: estado };
  return (
    <span style={{ ...bs, background: b.bg, color: b.color, fontSize: '10px',
      fontWeight: 700, padding: '3px 8px', borderRadius: '20px',
      letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      {b.label}
    </span>
  );
};

// ── Componente QR visual ───────────────────────────────────────────────────
const QRDisplay = ({ token }) => {
  const url    = `https://elededanza.com/boleto/${token}`;
  const qrSrc  = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}`;
  return (
    <div style={{ textAlign: 'center' }}>
      <img src={qrSrc} alt="QR Boleto" style={{ width: 160, height: 160,
        border: '4px solid #f0eaea', borderRadius: '4px' }} />
      <p style={{ ...bs, fontSize: '9px', color: '#bbb', marginTop: '6px',
        wordBreak: 'break-all', maxWidth: '180px' }}>{url}</p>
    </div>
  );
};

// ── Tab: Lista de boletos ──────────────────────────────────────────────────
const TabBoletos = ({ eventoId }) => {
  const [boletos,    setBoletos]    = useState([]);
  const [resumen,    setResumen]    = useState({});
  const [loading,    setLoading]    = useState(true);
  const [filtro,     setFiltro]     = useState('todos');
  const [search,     setSearch]     = useState('');
  const [detalle,    setDetalle]    = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/list_boletos.php?evento_id=${eventoId}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) { setBoletos(json.data); setResumen(json.resumen); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [eventoId]);

  useEffect(() => { load(); }, [load]);

  const handleConfirmar = async (boleto_id) => {
    if (!window.confirm('¿Confirmar pago y activar este boleto?')) return;
    setConfirming(boleto_id);
    try {
      const res  = await fetch(`${API}/confirmar_boleto.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ boleto_id }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      window.open(json.data.wa_link, '_blank');
      await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setConfirming(null); }
  };

  const filtrados = boletos.filter(b => {
    const matchFiltro = filtro === 'todos' || b.estado === filtro ||
      (filtro === 'cortesia' && b.tipo === 'cortesia');
    const matchSearch = !search ||
      b.nombre_comprador.toLowerCase().includes(search.toLowerCase()) ||
      b.whatsapp?.includes(search) ||
      b.asiento.toLowerCase().includes(search.toLowerCase());
    return matchFiltro && matchSearch;
  });

  const inp = {
    ...bs, padding: '8px 12px', border: '1px solid #e5e0e0', fontSize: '12px',
    color: '#060D14', outline: 'none', borderRadius: '2px', background: 'white',
    transition: 'border-color 0.2s',
  };

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: '10px',
        marginBottom: '20px' }}>
        {[
          { label: 'Total boletos',   val: resumen.total      || 0, color: '#060D14' },
          { label: 'Confirmados',     val: resumen.vendidos   || 0, color: '#16a34a' },
          { label: 'Cortesías',       val: resumen.cortesias  || 0, color: '#6366f1' },
          { label: 'Pendientes',      val: resumen.pendientes || 0, color: '#d97706' },
          { label: 'Usados (puerta)', val: resumen.usados     || 0, color: '#3730a3' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: 'white', border: '1px solid #f0eaea',
            borderRadius: '2px', padding: '12px 16px' }}>
            <p style={{ ...bs, fontSize: '9px', color: '#bbb', letterSpacing: '0.15em',
              textTransform: 'uppercase', margin: '0 0 4px' }}>{label}</p>
            <p style={{ ...ds, fontSize: '1.6rem', color, margin: 0 }}>{val}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { key: 'todos',     label: 'Todos' },
          { key: 'pendiente', label: 'Pendientes' },
          { key: 'confirmado',label: 'Confirmados' },
          { key: 'cortesia',  label: 'Cortesías' },
          { key: 'usado',     label: 'Usados' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFiltro(key)} style={{
            ...bs, background: filtro === key ? '#961914' : 'white',
            color: filtro === key ? 'white' : '#666',
            border: `1px solid ${filtro === key ? '#961914' : '#e5e0e0'}`,
            padding: '6px 14px', cursor: 'pointer', fontSize: '10px',
            fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
            borderRadius: '2px', transition: 'all 0.2s',
          }}>{label}</button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar nombre, asiento, WhatsApp…"
          style={{ ...inp, marginLeft: 'auto', width: '220px' }}
          onFocus={e => e.target.style.borderColor = '#961914'}
          onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
      </div>

      {/* Tabla */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ display: 'grid',
          gridTemplateColumns: '80px 1fr 100px 120px 100px 120px',
          padding: '10px 16px', background: '#faf9f9',
          borderBottom: '1px solid #f0eaea' }}>
          {['Asiento','Comprador','Tipo','Estado','Comprobante','Acciones'].map(h => (
            <span key={h} style={{ ...bs, fontSize: '9px', fontWeight: 700,
              letterSpacing: '0.15em', textTransform: 'uppercase', color: '#bbb' }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ ...bs, color: '#bbb', fontSize: '12px' }}>Cargando boletos…</p>
          </div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ ...bs, color: '#bbb', fontSize: '12px' }}>Sin resultados.</p>
          </div>
        ) : filtrados.map((b, i) => (
          <div key={b.id} style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr 100px 120px 100px 120px',
            padding: '12px 16px', alignItems: 'center',
            borderBottom: i < filtrados.length - 1 ? '1px solid #f8f5f5' : 'none',
            background: i % 2 === 0 ? 'white' : '#fdfafa',
          }}>
            <span style={{ ...ds, fontSize: '1rem', color: '#961914', fontWeight: 600 }}>
              {b.asiento}
            </span>
            <div>
              <p style={{ ...bs, fontSize: '12px', fontWeight: 600,
                color: '#060D14', margin: '0 0 1px' }}>{b.nombre_comprador}</p>
              <p style={{ ...bs, fontSize: '10px', color: '#bbb', margin: 0 }}>{b.whatsapp}</p>
            </div>
            <span style={{ ...bs, fontSize: '10px', fontWeight: 600,
              color: b.tipo === 'cortesia' ? '#6366f1' : '#666',
              textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {b.tipo === 'cortesia' ? '🎟 Cortesía' : '💳 Venta'}
            </span>
            <Badge estado={b.estado} />
            <div>
              {b.comprobante_url
                ? <a href={b.comprobante_url} target="_blank" rel="noreferrer"
                    style={{ ...bs, fontSize: '10px', color: '#961914', fontWeight: 600 }}>
                    Ver comprobante
                  </a>
                : <span style={{ ...bs, fontSize: '10px', color: '#ddd' }}>—</span>
              }
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {b.estado === 'pendiente' && (
                <button onClick={() => handleConfirmar(b.id)}
                  disabled={confirming === b.id}
                  style={{ ...bs, background: '#16a34a', color: 'white', border: 'none',
                    padding: '4px 10px', cursor: 'pointer', fontSize: '9px',
                    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    borderRadius: '2px', opacity: confirming === b.id ? 0.5 : 1 }}>
                  ✓ Confirmar
                </button>
              )}
              <button onClick={() => setDetalle(b)} style={{
                ...bs, background: 'none', border: '1px solid #e5e0e0',
                color: '#666', padding: '4px 10px', cursor: 'pointer',
                fontSize: '9px', fontWeight: 600, letterSpacing: '0.1em',
                textTransform: 'uppercase', borderRadius: '2px',
              }}>
                QR
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal detalle / QR */}
      {detalle && (
        <div onClick={e => { if (e.target === e.currentTarget) setDetalle(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(6,13,20,0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '2px', padding: '28px',
            maxWidth: '380px', width: '100%',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ ...ds, fontSize: '1.4rem', color: '#060D14', margin: 0 }}>
                Boleto #{detalle.id}
              </h3>
              <button onClick={() => setDetalle(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#aaa', fontSize: '18px' }}>×</button>
            </div>

            <QRDisplay token={detalle.qr_token} />

            <div style={{ marginTop: '16px', display: 'flex',
              flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Asiento',    val: detalle.asiento },
                { label: 'Nombre',     val: detalle.nombre_comprador },
                { label: 'WhatsApp',   val: detalle.whatsapp || '—' },
                { label: 'Tipo',       val: detalle.tipo },
                { label: 'Estado',     val: detalle.estado },
                { label: 'Confirmado', val: fmt(detalle.confirmado_at) },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                  borderBottom: '1px solid #f5f0f0', paddingBottom: '6px' }}>
                  <span style={{ ...bs, fontSize: '11px', color: '#bbb' }}>{label}</span>
                  <span style={{ ...bs, fontSize: '12px', color: '#060D14',
                    fontWeight: 500, textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>

            {detalle.estado === 'confirmado' && detalle.whatsapp && (
              <a href={`https://wa.me/${detalle.whatsapp.replace(/\D/g,'')}`}
                target="_blank" rel="noreferrer"
                style={{ ...bs, display: 'block', marginTop: '16px',
                  background: '#25D366', color: 'white', textDecoration: 'none',
                  padding: '12px', textAlign: 'center', borderRadius: '2px',
                  fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase' }}>
                📲 Abrir WhatsApp
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab: Asignar cortesías ─────────────────────────────────────────────────
const TabCortesias = ({ eventoId }) => {
  const [asientos,   setAsientos]   = useState([]);
  const [nombre,     setNombre]     = useState('');
  const [whatsapp,   setWhatsapp]   = useState('');
  const [notas,      setNotas]      = useState('');
  const [asientoSel, setAsientoSel] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [resultado,  setResultado]  = useState(null);

  useEffect(() => {
    fetch(`${API}/get_evento.php?id=${eventoId}`)
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          const libres = j.data.asientos.filter(a =>
            a.tipo === 'cortesia' && a.estado === 'libre'
          );
          setAsientos(libres);
        }
      });
  }, [eventoId, resultado]);

  const inp = {
    ...bs, width: '100%', padding: '9px 12px', border: '1px solid #e5e0e0',
    fontSize: '12px', color: '#060D14', outline: 'none', borderRadius: '2px',
    background: 'white', transition: 'border-color 0.2s', boxSizing: 'border-box',
  };

  const handleAsignar = async () => {
    if (!asientoSel || !nombre.trim() || !whatsapp.trim()) {
      alert('Completa asiento, nombre y WhatsApp'); return;
    }
    setSaving(true);
    try {
      const res  = await fetch(`${API}/asignar_cortesia.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ asiento_id: parseInt(asientoSel),
          evento_id: eventoId, nombre_comprador: nombre, whatsapp, notas }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      setResultado(json.data);
      setNombre(''); setWhatsapp(''); setNotas(''); setAsientoSel('');
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

      {/* Formulario */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', padding: '20px' }}>
        <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: '#961914', marginBottom: '16px' }}>
          Asignar Boleto de Cortesía
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', color: '#999',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '5px' }}>
              Asiento disponible ({asientos.length} libres)
            </label>
            <select value={asientoSel} onChange={e => setAsientoSel(e.target.value)}
              style={{ ...inp }}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
              <option value="">— Selecciona asiento —</option>
              {asientos.map(a => (
                <option key={a.id} value={a.id}>
                  Fila {a.fila} — Asiento {a.numero}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', color: '#999',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '5px' }}>
              Nombre completo *
            </label>
            <input style={inp} value={nombre} onChange={e => setNombre(e.target.value)}
              placeholder="Nombre del padre/familiar"
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', color: '#999',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '5px' }}>
              WhatsApp *
            </label>
            <input style={inp} value={whatsapp} onChange={e => setWhatsapp(e.target.value)}
              placeholder="55 1234 5678"
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', color: '#999',
              letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '5px' }}>
              Nota (opcional)
            </label>
            <input style={inp} value={notas} onChange={e => setNotas(e.target.value)}
              placeholder="Ej: Cortesía alumna María García"
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <button onClick={handleAsignar} disabled={saving} style={{
            ...bs, background: '#6366f1', color: 'white', border: 'none',
            padding: '11px', cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em',
            textTransform: 'uppercase', borderRadius: '2px',
            opacity: saving ? 0.7 : 1, transition: 'background 0.2s',
          }}
          onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#4f46e5'; }}
          onMouseOut={e => e.currentTarget.style.background = '#6366f1'}>
            {saving ? 'Asignando…' : '🎟 Asignar Cortesía y Enviar QR'}
          </button>
        </div>
      </div>

      {/* Resultado */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', padding: '20px' }}>
        <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: '#bbb', marginBottom: '16px' }}>
          Último boleto asignado
        </p>
        {resultado ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{ background: 'white', padding: '12px',
                border: '2px solid #6366f1', borderRadius: '4px' }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent('https://elededanza.com/boleto/' + resultado.qr_token)}`}
                  alt="QR Cortesía" style={{ width: 140, height: 140, display: 'block' }} />
              </div>
            </div>
            <a href={resultado.wa_link} target="_blank" rel="noreferrer"
              style={{ ...bs, display: 'block', background: '#25D366', color: 'white',
                textDecoration: 'none', padding: '12px', textAlign: 'center',
                borderRadius: '2px', fontSize: '11px', fontWeight: 700,
                letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              📲 Enviar por WhatsApp
            </a>
          </>
        ) : (
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', textAlign: 'center',
            marginTop: '40px' }}>
            Asigna un boleto para ver el QR aquí
          </p>
        )}
      </div>
    </div>
  );
};

// ── Tab: Validar QR en puerta ──────────────────────────────────────────────
// FIX 3: Lector de cámara con html5-qrcode (cargado en public/index.html)
const TabValidar = ({ eventoId }) => {
  const [token,    setToken]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null); // instancia de Html5Qrcode

  // Limpiar el scanner si el componente se desmonta mientras escanea
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const handleValidar = async (rawToken) => {
    const t = (rawToken || token).trim().split('/').pop(); // acepta URL completa o solo token
    if (!t) return;
    setLoading(true); setResult(null); setError(null);
    try {
      const res  = await fetch(`${API}/validar_qr.php?token=${t}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) setResult(json.data);
      else              setError(json.message);
    } catch (e) { setError('Error de red'); }
    finally { setLoading(false); setToken(''); }
  };

  const handleEscanear = () => {
    // Verificar que la librería esté disponible (cargada en index.html)
    if (!window.Html5Qrcode) {
      alert('El lector de cámara no está disponible. Verifica que html5-qrcode esté incluido en index.html.');
      return;
    }

    setScanning(true);
    setResult(null);
    setError(null);

    // Pequeño delay para que el DOM renderice el div #qr-reader antes de montarlo
    setTimeout(() => {
      const html5QrCode = new window.Html5Qrcode('qr-reader');
      scannerRef.current = html5QrCode;

      html5QrCode.start(
        { facingMode: 'environment' }, // cámara trasera en móvil
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          // QR detectado — detener cámara y validar
          html5QrCode.stop().then(() => {
            scannerRef.current = null;
            setScanning(false);
            handleValidar(decodedText);
          }).catch(() => {
            setScanning(false);
            handleValidar(decodedText);
          });
        },
        () => {} // error de frame — silencioso, es normal durante el escaneo
      ).catch((err) => {
        setScanning(false);
        scannerRef.current = null;
        setError('No se pudo acceder a la cámara. Verifica los permisos del navegador.');
        console.error('Camera error:', err);
      });
    }, 100);
  };

  const handleDetenerCamara = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  return (
    <div style={{ maxWidth: '480px', margin: '0 auto' }}>
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', padding: '24px', marginBottom: '16px' }}>
        <p style={{ ...bs, fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: '#961914', marginBottom: '16px' }}>
          Validar Boleto en Puerta
        </p>

        {/* FIX 3: Botón de cámara */}
        {!scanning ? (
          <button onClick={handleEscanear} style={{
            ...bs, width: '100%', background: '#060D14', color: 'white', border: 'none',
            padding: '14px', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
            letterSpacing: '0.15em', textTransform: 'uppercase', borderRadius: '2px',
            marginBottom: '16px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '8px',
          }}>
            📷 Escanear QR con Cámara
          </button>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            {/* Contenedor del scanner — html5-qrcode monta aquí el video */}
            <div id="qr-reader" style={{ width: '100%', borderRadius: '4px',
              overflow: 'hidden', border: '2px solid #961914' }} />
            <button onClick={handleDetenerCamara} style={{
              ...bs, width: '100%', background: '#fee2e2', color: '#991b1b',
              border: '1px solid #fca5a5', padding: '10px', cursor: 'pointer',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', borderRadius: '2px', marginTop: '8px',
            }}>
              ✕ Cancelar escaneo
            </button>
          </div>
        )}

        {/* Separador */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
          margin: '4px 0 16px' }}>
          <div style={{ flex: 1, height: '1px', background: '#f0eaea' }} />
          <span style={{ ...bs, fontSize: '10px', color: '#ccc',
            letterSpacing: '0.1em', textTransform: 'uppercase' }}>o pega el token</span>
          <div style={{ flex: 1, height: '1px', background: '#f0eaea' }} />
        </div>

        {/* Input manual */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <input value={token} onChange={e => setToken(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleValidar()}
            placeholder="Token o URL del QR…"
            style={{ ...bs, flex: 1, padding: '10px 14px', border: '1px solid #e5e0e0',
              fontSize: '12px', outline: 'none', borderRadius: '2px',
              transition: 'border-color 0.2s' }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <button onClick={() => handleValidar()} disabled={loading || !token.trim()} style={{
            ...bs, background: '#961914', color: 'white', border: 'none',
            padding: '10px 20px', cursor: 'pointer', fontSize: '11px',
            fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            borderRadius: '2px', opacity: loading ? 0.7 : 1,
          }}>
            {loading ? '…' : 'Validar'}
          </button>
        </div>
      </div>

      {result && (
        <div style={{ background: '#f0fdf4', border: '2px solid #86efac',
          borderRadius: '4px', padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
          <h3 style={{ ...ds, fontSize: '1.6rem', color: '#16a34a', margin: '0 0 12px' }}>
            Acceso Autorizado
          </h3>
          {[
            { label: 'Nombre',  val: result.nombre },
            { label: 'Asiento', val: result.asiento },
            { label: 'Tipo',    val: result.tipo === 'cortesia' ? '🎟 Cortesía' : '💳 Venta' },
          ].map(({ label, val }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ ...bs, fontSize: '12px', color: '#666' }}>{label}</span>
              <span style={{ ...bs, fontSize: '12px', fontWeight: 600, color: '#060D14' }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '2px solid #fca5a5',
          borderRadius: '4px', padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>⛔</div>
          <h3 style={{ ...ds, fontSize: '1.4rem', color: '#dc2626', margin: '0 0 8px' }}>
            Acceso Denegado
          </h3>
          <p style={{ ...bs, fontSize: '13px', color: '#666', margin: 0 }}>{error}</p>
        </div>
      )}
    </div>
  );
};

// ── Tab: Editar evento ────────────────────────────────────────────────────
const TabEditar = ({ evento, onSaved }) => {
  const [form, setForm] = useState({
    nombre:      evento?.nombre      || '',
    descripcion: evento?.descripcion || '',
    fecha:       evento?.fecha?.slice(0,16) || '',
    lugar:       evento?.lugar       || '',
    precio_base: evento?.precio_base || 100,
    activo:      evento?.activo !== undefined ? Boolean(Number(evento.activo)) : true,
    mp_link:     evento?.mp_link     || '',
  });
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const inp = {
    ...bs, width: '100%', padding: '10px 14px', border: '1px solid #e5e0e0',
    fontSize: '13px', color: '#060D14', outline: 'none', borderRadius: '2px',
    background: 'white', transition: 'border-color 0.2s', boxSizing: 'border-box',
  };
  const lbl = {
    ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.15em',
    textTransform: 'uppercase', color: '#999', marginBottom: '5px',
  };
  const fr = e => { e.target.style.borderColor = '#961914'; };
  const fb = e => { e.target.style.borderColor = '#e5e0e0'; };

  const handleSave = async () => {
    if (!form.nombre || !form.fecha || !form.lugar) {
      alert('Nombre, fecha y lugar son obligatorios'); return;
    }
    setSaving(true);
    try {
      const body = { ...form, ...(evento?.id ? { id: evento.id } : {}) };
      const res  = await fetch(`${API}/save_evento.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      alert('✓ Evento guardado correctamente');
      if (onSaved) onSaved();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', padding: '24px',
        display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <div>
          <label style={lbl}>Nombre del evento *</label>
          <input style={inp} value={form.nombre}
            onChange={e => setF('nombre', e.target.value)}
            onFocus={fr} onBlur={fb}
            placeholder="Presentación de Fin de Ciclo — ELEDÉ 2025" />
        </div>

        <div>
          <label style={lbl}>Descripción (aparece en el sitio público)</label>
          <textarea style={{ ...inp, resize: 'vertical', height: '80px' }}
            value={form.descripcion}
            onChange={e => setF('descripcion', e.target.value)}
            onFocus={fr} onBlur={fb}
            placeholder="Describe el evento para los visitantes…" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={lbl}>Fecha y hora *</label>
            <input type="datetime-local" style={inp} value={form.fecha}
              onChange={e => setF('fecha', e.target.value)}
              onFocus={fr} onBlur={fb} />
          </div>
          <div>
            <label style={lbl}>Precio del boleto (MXN) *</label>
            <input type="number" min="0" step="1" style={inp}
              value={form.precio_base}
              onChange={e => setF('precio_base', e.target.value)}
              onFocus={fr} onBlur={fb} />
          </div>
        </div>

        <div>
          <label style={lbl}>Lugar / Dirección *</label>
          <input style={inp} value={form.lugar}
            onChange={e => setF('lugar', e.target.value)}
            onFocus={fr} onBlur={fb}
            placeholder="Teatro 2 Centenario Coyoacán — Constitución s/n, CDMX" />
        </div>

        <div>
          <label style={lbl}>Link de pago MercadoPago</label>
          <input style={inp} value={form.mp_link}
            onChange={e => setF('mp_link', e.target.value)}
            onFocus={fr} onBlur={fb}
            placeholder="https://mpago.la/xxxxxxx" />
          <p style={{ ...bs, fontSize: '10px', color: '#bbb', margin: '4px 0 0',
            fontStyle: 'italic' }}>
            Genera tu link en mercadopago.com.mx → Tu negocio → Cobros → Botón de pago
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input type="checkbox" id="activo" checked={form.activo}
            onChange={e => setF('activo', e.target.checked)}
            style={{ accentColor: '#961914', width: '16px', height: '16px' }} />
          <label htmlFor="activo" style={{ ...bs, fontSize: '13px', color: '#060D14',
            cursor: 'pointer' }}>
            Evento activo (visible en el sitio público)
          </label>
        </div>

        <button onClick={handleSave} disabled={saving} style={{
          ...bs, background: '#961914', color: 'white', border: 'none',
          padding: '12px', cursor: saving ? 'not-allowed' : 'pointer',
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em',
          textTransform: 'uppercase', borderRadius: '2px',
          opacity: saving ? 0.7 : 1, transition: 'background 0.2s',
          alignSelf: 'flex-end', minWidth: '160px',
        }}
        onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#7a140f'; }}
        onMouseOut={e => e.currentTarget.style.background = '#961914'}>
          {saving ? 'Guardando…' : 'Guardar Cambios'}
        </button>
      </div>
    </div>
  );
};

// ── Página principal admin ─────────────────────────────────────────────────
const AdminEventosPage = () => {
  const [eventos,  setEventos]  = useState([]);
  const [eventoId, setEventoId] = useState(null);
  const [tab,      setTab]      = useState('boletos');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    fetch(`${API}/get_evento.php`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setEventos([j.data.evento]);
          setEventoId(parseInt(j.data.evento.id));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const evento = eventos.find(e => parseInt(e.id) === eventoId);

  if (loading) return (
    <div style={{ padding: '48px', textAlign: 'center' }}>
      <p style={{ ...bs, color: '#bbb', fontSize: '12px' }}>Cargando…</p>
    </div>
  );

  return (
    <div style={{ padding: '24px 16px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400, color: '#060D14', margin: '0 0 4px' }}>
          Gestión de Eventos
        </h1>
        {evento && (
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', margin: 0 }}>
            {evento.nombre} · {new Date(evento.fecha.replace(' ', 'T'))
              .toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
            {' · '}{evento.lugar}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #f0eaea', marginBottom: '24px',
        display: 'flex', gap: '2px' }}>
        {[
          { id: 'boletos',   label: '🎟 Boletos vendidos' },
          { id: 'cortesias', label: '🎁 Asignar cortesías' },
          { id: 'validar',   label: '📱 Validar en puerta' },
          { id: 'editar',    label: '✏️ Editar evento' },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            ...bs, background: 'none', border: 'none', cursor: 'pointer',
            padding: '12px 16px', fontSize: '11px', fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: tab === id ? '#961914' : '#aaa',
            borderBottom: tab === id ? '2px solid #961914' : '2px solid transparent',
            marginBottom: '-1px', transition: 'all 0.2s', whiteSpace: 'nowrap',
          }}>{label}</button>
        ))}
      </div>

      {eventoId && tab === 'boletos'   && <TabBoletos   eventoId={eventoId} />}
      {eventoId && tab === 'cortesias' && <TabCortesias eventoId={eventoId} />}
      {eventoId && tab === 'validar'   && <TabValidar   eventoId={eventoId} />}
      {           tab === 'editar'     && <TabEditar    evento={evento}
        onSaved={() => window.location.reload()} />}
    </div>
  );
};

export default AdminEventosPage;
